import React from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { LineChart, Line, ResponsiveContainer } from 'recharts';
import { MetricData, getBenchmarkStatus } from '../data/mockData';
interface MetricCardProps {
  metric: MetricData;
  onClick?: () => void;
}
export function MetricCard({
  metric,
  onClick
}: MetricCardProps) {
  const isPositive = metric.trend === 'up';
  const isNeutral = metric.trend === 'neutral';
  const isNegative = metric.trend === 'down';
  // Benchmark-based coloring
  const benchmarkStatus = getBenchmarkStatus(metric.value, metric.benchmark);
  const numValue = typeof metric.value === 'string' ? parseFloat(metric.value) : metric.value;
  const benchmarkPct = Math.min(Math.round(numValue / metric.benchmark * 100), 150);
  const statusColors = {
    green: {
      glow: 'bg-emerald-500',
      border: 'hover:border-emerald-500/40',
      text: 'text-emerald-400',
      sparkline: '#34d399',
      progressBg: 'bg-emerald-500/20',
      progressFill: 'bg-emerald-500',
      badge: 'bg-emerald-500/10 text-emerald-400'
    },
    yellow: {
      glow: 'bg-amber-500',
      border: 'hover:border-amber-500/40',
      text: 'text-amber-400',
      sparkline: '#fbbf24',
      progressBg: 'bg-amber-500/20',
      progressFill: 'bg-amber-500',
      badge: 'bg-amber-500/10 text-amber-400'
    },
    red: {
      glow: 'bg-red-500',
      border: 'hover:border-red-500/40',
      text: 'text-red-400',
      sparkline: '#f87171',
      progressBg: 'bg-red-500/20',
      progressFill: 'bg-red-500',
      badge: 'bg-red-500/10 text-red-400'
    }
  };
  const colors = statusColors[benchmarkStatus];
  // Trend arrow colors remain based on actual trend direction
  const trendColor = isPositive ? 'text-emerald-400' : isNegative ? 'text-red-400' : 'text-slate-400';
  return <div onClick={onClick} className={`bg-slate-800/80 border border-slate-700/50 rounded-xl p-5 hover:bg-slate-800 ${colors.border} transition-all duration-200 shadow-lg group relative ${onClick ? 'cursor-pointer' : ''}`} data-id="element-161">
      {/* Top glow based on benchmark status */}
      <div className="absolute top-0 left-0 w-full overflow-hidden rounded-t-xl" data-id="element-162">
        <div className={`w-full h-0.5 opacity-60 group-hover:opacity-100 transition-opacity ${colors.glow}`} data-id="element-163" />
      </div>

      <div className="flex justify-between items-start mb-1" data-id="element-164">
        <h3 className="text-slate-400 text-sm font-medium" data-id="element-165">{metric.name}</h3>
        <div className={`relative flex items-center text-xs font-semibold ${trendColor} bg-slate-900/50 px-2 py-1 rounded-md cursor-help group/trend`} title={`${metric.trendValue} change vs. prior period`} data-id="element-166">
          {isPositive && <TrendingUp className="w-3 h-3 mr-1" data-id="element-167" />}
          {isNegative && <TrendingDown className="w-3 h-3 mr-1" data-id="element-168" />}
          {isNeutral && <Minus className="w-3 h-3 mr-1" data-id="element-169" />}
          {metric.trendValue}
          <div className="absolute bottom-full right-0 mb-2 px-3 py-1.5 bg-slate-950 border border-slate-700 text-slate-200 text-xs rounded-lg shadow-xl whitespace-nowrap opacity-0 pointer-events-none group-hover/trend:opacity-100 transition-opacity duration-200 z-20" data-id="element-170">
            Trend: {metric.trendValue} change vs. prior period
            <div className="absolute top-full right-3 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-slate-700" data-id="element-171"></div>
          </div>
        </div>
      </div>

      <div className="flex items-end justify-between mt-3" data-id="element-172">
        <div className="text-3xl font-bold text-white tracking-tight" data-id="element-173">
          {metric.value}
        </div>

        <div className="w-20 h-10 opacity-70 group-hover:opacity-100 transition-opacity" data-id="element-174">
          <ResponsiveContainer width="100%" height="100%" data-id="element-175">
            <LineChart data={metric.sparklineData} data-id="element-176">
              <Line type="monotone" dataKey="value" stroke={colors.sparkline} strokeWidth={2} dot={false} isAnimationActive={false} data-id="element-177" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Benchmark indicator */}
      <div className="mt-3 pt-3 border-t border-slate-700/30" data-id="element-178">
        <div className="flex items-center justify-between mb-1.5" data-id="element-179">
          <span className="text-xs text-slate-400 font-medium" data-id="element-180">
            Benchmark: {metric.benchmark}
          </span>
          <span className={`text-[11px] font-bold ${colors.text}`} data-id="element-181">
            {benchmarkPct}%
          </span>
        </div>
        <div className={`h-1.5 w-full rounded-full ${colors.progressBg}`} data-id="element-182">
          <div className={`h-full rounded-full ${colors.progressFill} transition-all duration-500`} style={{
          width: `${Math.min(benchmarkPct, 100)}%`
        }} data-id="element-183" />
        </div>
      </div>
    </div>;
}